import { useLoanLock } from '@src/providers/LoanLockProvider'
import { EOperationType } from '@src/types/loan/loan-operation'
import {
  getCancellableProductRefund,
  getCancelledFee,
  getEditableFee,
  SignedCalendarSimulation,
} from '@src/types/loan/loan-simulation'
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  useAddFeeOnCalendar,
  useCancelFeeOnCalendar,
  useEditFeeOnCalendar,
  useReapplyFeeOnCalendar,
} from './EditFees/Fees-hooks'
import { EEditPaymentsSubAction, useEditPaymentsDialog } from './EditPayments/EditPaymentsDialog'
import { useLoanCalendarChangeRegularPmtDialog } from './LoanCalendarChangeRegularPmt/LoanCalendarChangeRegularPmtDialog-hook'
import { useLoanCalendarChargeOffDialog } from './LoanCalendarChargeOff/LoanCalendarChargeOffDialog-hook'
import { useDeleteChargeOffOnCalendar } from './LoanCalendarChargeOff/LoanCalendarDeleteChargeOff-hook'
import { useLoanCalendarFrequencyDialog } from './LoanCalendarFrequency/LoanCalendarFrequencyDialog-hook'
import { useLoanCalendarPaymentStatusDialog } from './LoanCalendarPaymentStatus/LoanCalendarPaymentStatusDialog'
import { useCancelProductRefundOnCalendar } from './LoanCalendarProductRefund/LoanCalendarCancelProductRefund-hook'
import { useLoanCalendarProductRefundDialog } from './LoanCalendarProductRefund/LoanCalendarProductRefundDialog-hook'
import { useLoanCalendarResetDialog } from './LoanCalendarResetDialog-hook'
import { useLoanCalendarSimulation } from './LoanCalendarSimulationProvider'
import { useLoanMoveCalendarDialog } from './LoanMoveCalendar/LoanMoveCalendarDialog-hook'

export enum ELoanCalendarAction {
  OpenUpdatePaymentFrequencyDialog = 'openUpdatePaymentFrequencyDialog',
  OpenMoveCalendarDialog = 'openMoveCalendarDialog',
  OpenResetPaymentCalendarDialog = 'openResetPaymentCalendarDialog',
  OpenEditPaymentsDialog = 'openEditPaymentsDialog',
  OpenAddRecurringPaymentsDialog = 'openAddRecurringPaymentsDialog',
  OpenSpreadOverLateAmountDialog = 'openSpreadOverLateAmountDialog',
  OpenAddPaymentDialog = 'openAddPaymentDialog',
  OpenDeletePaymentsDialog = 'openDeletePaymentsDialog',
  OpenCatchUpAmountOfOverdueForDate = 'openCatchUpAmountOfOverdueForDate',
  OpenChangePaymentStatusDialog = 'openChangePaymentStatusDialog',
  OpenMoveSelectedPaymentsDialog = 'openMoveSelectedPaymentsDialog',
  OpenAmountToReplaceForDate = 'openAmountToReplaceForDate',
  OpenBatchUpdatePaymentsDialog = 'openBatchUpdatePaymentsDialog',
  OpenIncreasePaymentsDialog = 'openIncreasePaymentsDialog',
  OpenDecreasePaymentsDialog = 'openDecreasePaymentsDialog',
  OpenCatchUpOnRemainingPaymentsDialog = 'openCatchUpOnRemainingPaymentsDialog',
  OpenAddFeeDialog = 'openAddFeeDialog',
  OpenEditFeeDialog = 'openEditFeeDialog',
  OpenCancelFeeDialog = 'openCancelFeeDialog',
  ReapplyFee = 'reapplyFee',
  OpenChangeRegularPmtDialog = 'openChangeRegularPmtDialog',
  OpenChargeOffDialog = 'openChargeOffDialog',
  OpenDeleteChargeOffDialog = 'openDeleteChargeOffDialog',
  OpenProductRefundDialog = 'openProductRefundDialog',
  OpenCancelProductRefundDialog = 'openCancelProductRefundDialog',
}

const editPaymentsActions = new Set<ELoanCalendarAction>([
  ELoanCalendarAction.OpenEditPaymentsDialog,
  ELoanCalendarAction.OpenAddRecurringPaymentsDialog,
  ELoanCalendarAction.OpenAddPaymentDialog,
  ELoanCalendarAction.OpenDeletePaymentsDialog,
  ELoanCalendarAction.OpenCatchUpAmountOfOverdueForDate,
  ELoanCalendarAction.OpenSpreadOverLateAmountDialog,
  ELoanCalendarAction.OpenMoveSelectedPaymentsDialog,
  ELoanCalendarAction.OpenAmountToReplaceForDate,
  ELoanCalendarAction.OpenBatchUpdatePaymentsDialog,
  ELoanCalendarAction.OpenIncreasePaymentsDialog,
  ELoanCalendarAction.OpenDecreasePaymentsDialog,
  ELoanCalendarAction.OpenCatchUpOnRemainingPaymentsDialog,
])

const editPaymentsSubAction = new Map<ELoanCalendarAction, EEditPaymentsSubAction>([
  [ELoanCalendarAction.OpenAddRecurringPaymentsDialog, EEditPaymentsSubAction.AddRecurringPayments],
  [ELoanCalendarAction.OpenAddPaymentDialog, EEditPaymentsSubAction.AddPayment],
  [ELoanCalendarAction.OpenDeletePaymentsDialog, EEditPaymentsSubAction.DeletePayment],
  [ELoanCalendarAction.OpenSpreadOverLateAmountDialog, EEditPaymentsSubAction.SpreadLateAmount],
  [ELoanCalendarAction.OpenCatchUpAmountOfOverdueForDate, EEditPaymentsSubAction.CatchUpAmountOfOverdueForDate],
  [ELoanCalendarAction.OpenMoveSelectedPaymentsDialog, EEditPaymentsSubAction.MovePayments],
  [ELoanCalendarAction.OpenBatchUpdatePaymentsDialog, EEditPaymentsSubAction.BatchUpdatePayments],
  [ELoanCalendarAction.OpenIncreasePaymentsDialog, EEditPaymentsSubAction.BatchUpdatePayments],
  [ELoanCalendarAction.OpenDecreasePaymentsDialog, EEditPaymentsSubAction.BatchUpdatePayments],
  [ELoanCalendarAction.OpenCatchUpOnRemainingPaymentsDialog, EEditPaymentsSubAction.BatchUpdatePayments],
])

interface ILoanCalendarActionContext {
  action: ELoanCalendarAction | null
  dispatchAction: (action: ELoanCalendarAction | null) => void
  selectedPaymentIds: Set<string>
  setSelectedPaymentIds: (payments: Set<string>) => void
  clearSelection: () => void
}

const LoanCalendarActionContext = createContext<ILoanCalendarActionContext | null>(null)

export const useLoanCalendarAction = () => {
  const ctx = useContext(LoanCalendarActionContext)
  if (!ctx) throw new Error('useLoanCalendarAction must be used within a LoanCalendarActionProvider')
  return ctx
}

interface LoanCalendarActionProviderProps {
  children: ReactNode
}

const LoanCalendarActionProvider = ({ children }: LoanCalendarActionProviderProps) => {
  const { originalSimulation, currentSimulation, addSimulationAction } = useLoanCalendarSimulation()
  const { isReadOnly } = useLoanLock()
  const [action, setAction] = useState<ELoanCalendarAction | null>(null)
  const [selectedPaymentIds, setSelectedPaymentIds] = useState<Set<string>>(new Set())
  const navigate = useNavigate()
  const location = useLocation()

  const handleActionClose = useCallback(() => setAction(null), [])

  const clearSelection = useCallback(() => {
    setSelectedPaymentIds(new Set())
  }, [])

  // selectedPaymentIds are simulatedOperationId values, which CalendarBuilder regenerates on every
  // simulation build. Once currentSimulation is replaced (apply/reset/refresh), the previous ids
  // match nothing, so a lingering selection is stale. Clear it whenever the simulation changes.
  useEffect(() => {
    clearSelection()
  }, [currentSimulation, clearSelection])

  const ctx = useMemo(
    () => ({
      action,
      dispatchAction: setAction,
      selectedPaymentIds,
      setSelectedPaymentIds,
      clearSelection,
    }),
    [action, selectedPaymentIds, clearSelection],
  )

  const closeDialogAndClearSelection = useCallback(() => {
    setAction(null)
    clearSelection()
  }, [clearSelection])

  const editPaymentsDialog = useEditPaymentsDialog()
  const frequencyDialog = useLoanCalendarFrequencyDialog(currentSimulation, isReadOnly)
  const changeRegularPmtDialog = useLoanCalendarChangeRegularPmtDialog(currentSimulation, isReadOnly)
  const moveCalendarDialog = useLoanMoveCalendarDialog(currentSimulation, isReadOnly)
  const chargeOffDialog = useLoanCalendarChargeOffDialog(currentSimulation, isReadOnly)
  const deleteChargeOff = useDeleteChargeOffOnCalendar(currentSimulation, isReadOnly)
  const productRefundDialog = useLoanCalendarProductRefundDialog(currentSimulation, isReadOnly)
  const cancelProductRefund = useCancelProductRefundOnCalendar(currentSimulation, isReadOnly)
  const paymentStatusDialog = useLoanCalendarPaymentStatusDialog(currentSimulation, selectedPaymentIds, isReadOnly)
  const addFee = useAddFeeOnCalendar(currentSimulation, isReadOnly)
  const editFee = useEditFeeOnCalendar(currentSimulation, isReadOnly)
  const cancelFee = useCancelFeeOnCalendar(currentSimulation, isReadOnly)
  const reapplyFee = useReapplyFeeOnCalendar(currentSimulation)

  // The fee of the row the agent acted on: a row action selects that row alone.
  const getSelectedOperation = useCallback(
    () =>
      currentSimulation.simulation.operationInformations.find((opInfo) =>
        selectedPaymentIds.has(opInfo.simulatedOperationId),
      )?.operation,
    [currentSimulation, selectedPaymentIds],
  )
  const getSelectedFee = useCallback(() => {
    const operation = getSelectedOperation()
    return operation ? getEditableFee(operation) : null
  }, [getSelectedOperation])
  const getSelectedCancelledFee = useCallback(() => {
    const operation = getSelectedOperation()
    return operation ? getCancelledFee(operation) : null
  }, [getSelectedOperation])
  const getSelectedChargeOff = useCallback(() => {
    const operation = getSelectedOperation()
    return operation?._internalType === EOperationType.ChargeOffOperation ? operation : null
  }, [getSelectedOperation])
  const getSelectedCancellableProductRefund = useCallback(() => {
    const operation = getSelectedOperation()
    return operation ? getCancellableProductRefund(operation) : null
  }, [getSelectedOperation])
  // Reset undoes the whole session, so it is the only one that works off the calendar as the
  // page loaded it rather than the one the pending actions have built up.
  const resetDialog = useLoanCalendarResetDialog(originalSimulation, isReadOnly)

  // The calendar-wide dialogs are declared once and opened on demand; this is what turns
  // a dispatched action into the opener that owns it.
  const calendarDialogs = useMemo<Partial<Record<ELoanCalendarAction, () => Promise<SignedCalendarSimulation | null>>>>(
    () => ({
      [ELoanCalendarAction.OpenUpdatePaymentFrequencyDialog]: () => frequencyDialog.create(),
      [ELoanCalendarAction.OpenChangeRegularPmtDialog]: () => changeRegularPmtDialog.create(),
      [ELoanCalendarAction.OpenMoveCalendarDialog]: () => moveCalendarDialog.create(),
      [ELoanCalendarAction.OpenChargeOffDialog]: () => chargeOffDialog.create(),
      [ELoanCalendarAction.OpenProductRefundDialog]: () => productRefundDialog.create(),
      [ELoanCalendarAction.OpenResetPaymentCalendarDialog]: () => resetDialog.open(),
      // The only one opened on a selection, so it is the only one that drops the selection
      // once it closes — confirmed or not.
      [ELoanCalendarAction.OpenChangePaymentStatusDialog]: () => paymentStatusDialog.create().finally(clearSelection),
      [ELoanCalendarAction.OpenAddFeeDialog]: () => addFee(),
      // Opened from a fee's row, which the selection names.
      [ELoanCalendarAction.OpenEditFeeDialog]: async () => {
        const fee = getSelectedFee()
        if (!fee) return null
        return editFee(fee).finally(clearSelection)
      },
      [ELoanCalendarAction.OpenCancelFeeDialog]: async () => {
        const fee = getSelectedFee()
        if (!fee) return null
        return cancelFee(fee).finally(clearSelection)
      },
      [ELoanCalendarAction.OpenDeleteChargeOffDialog]: async () => {
        const chargeOff = getSelectedChargeOff()
        if (!chargeOff) return null
        return deleteChargeOff(chargeOff).finally(clearSelection)
      },
      [ELoanCalendarAction.ReapplyFee]: async () => {
        const fee = getSelectedCancelledFee()
        // No dialog to hold Confirm shut, so the lock is checked here.
        if (!fee || isReadOnly) return null
        return reapplyFee(fee).finally(clearSelection)
      },
      // Opened from a refund's row, which the selection names.
      [ELoanCalendarAction.OpenCancelProductRefundDialog]: async () => {
        const refund = getSelectedCancellableProductRefund()
        if (!refund) return null
        return cancelProductRefund(refund).finally(clearSelection)
      },
    }),
    [
      addFee,
      cancelFee,
      cancelProductRefund,
      changeRegularPmtDialog,
      clearSelection,
      deleteChargeOff,
      editFee,
      getSelectedFee,
      getSelectedCancelledFee,
      getSelectedChargeOff,
      getSelectedCancellableProductRefund,
      frequencyDialog,
      isReadOnly,
      moveCalendarDialog,
      paymentStatusDialog,
      reapplyFee,
      resetDialog,
      chargeOffDialog,
      productRefundDialog,
    ],
  )

  const openedActionRef = useRef<ELoanCalendarAction | null>(null)
  const openedCalendarActionRef = useRef<ELoanCalendarAction | null>(null)
  const closedByHistoryRef = useRef(false)
  const isMountedRef = useRef(true)

  useEffect(
    () => () => {
      isMountedRef.current = false
    },
    [],
  )

  useEffect(() => {
    if (!action || !editPaymentsActions.has(action) || openedActionRef.current === action) return
    openedActionRef.current = action

    navigate(location.pathname + location.search, { state: { editPaymentDialog: true }, replace: false })

    void editPaymentsDialog
      .open({
        initialSignedSimulation: currentSimulation,
        selectedPaymentIds,
        defaultSubAction: editPaymentsSubAction.get(action) ?? undefined,
        isCatchUpMode: action === ELoanCalendarAction.OpenCatchUpOnRemainingPaymentsDialog,
        isIncreasingPayments: action !== ELoanCalendarAction.OpenDecreasePaymentsDialog,
        isLockedByOther: isReadOnly,
      })
      .then((confirmed) => {
        openedActionRef.current = null
        // The design system resolves this promise with null when the declaring component
        // unmounts, so without this guard an unmounted provider would still navigate.
        if (!isMountedRef.current) return
        if (confirmed) addSimulationAction(confirmed)
        closeDialogAndClearSelection()
        if (!closedByHistoryRef.current) navigate(-1)
        closedByHistoryRef.current = false
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action])

  useEffect(() => {
    const openDialog = action ? calendarDialogs[action] : undefined
    if (!action || !openDialog || openedCalendarActionRef.current === action) return
    openedCalendarActionRef.current = action

    void openDialog().then((confirmed) => {
      openedCalendarActionRef.current = null
      // The design system resolves this promise with null when the declaring component
      // unmounts, so without this guard an unmounted provider would still dispatch.
      if (!isMountedRef.current) return
      if (confirmed) addSimulationAction(confirmed)
      handleActionClose()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action])

  useEffect(() => {
    const handlePopState = () => {
      if (!openedActionRef.current) return
      closedByHistoryRef.current = true
      editPaymentsDialog.close()
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [editPaymentsDialog])

  return <LoanCalendarActionContext.Provider value={ctx}>{children}</LoanCalendarActionContext.Provider>
}

export default LoanCalendarActionProvider
